import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";

export const Container = ({ className, children }) => (
  <div className={cn("mx-auto w-full max-w-[1320px] px-5 sm:px-8 lg:px-12", className)}>{children}</div>
);

export const DraftTag = ({ label = "Draft copy", className }) => (
  <span className={cn("draft-tag", className)}>{label}</span>
);

export const Mono = ({ children, className, accent }) => (
  <span className={cn("font-mono text-[10.5px] uppercase tracking-[0.2em]", accent ? "text-o-accent" : "text-o-dim", className)}>
    {children}
  </span>
);

export const Eyebrow = ({ index, children, draft = true, className }) => (
  <div className={cn("flex flex-wrap items-center gap-3 font-mono text-[11px] uppercase tracking-[0.22em] text-o-dim", className)}>
    {index && <span className="text-o-accent">{index}</span>}
    {index && <span aria-hidden className="h-px w-8 bg-o-strong" />}
    <span>{children}</span>
    {draft && <DraftTag />}
  </div>
);

const VARIANTS = {
  primary: "bg-o-accent text-o-accent-ink hover:brightness-110",
  line: "border border-o-strong text-o-text hover:border-o-accent hover:bg-o-soft",
};
const BTN = "group inline-flex items-center justify-between gap-4 px-5 py-3 text-sm font-medium tracking-wide transition-[background-color,border-color,filter] duration-200";
const Arrow = () => <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />;

export const ActionLink = ({ to, state, variant = "primary", testid, children, className }) => (
  <Link to={to} state={state} data-testid={testid} className={cn(BTN, VARIANTS[variant], className)}>
    <span>{children}</span>
    <Arrow />
  </Link>
);

export const ActionAnchor = ({ href, variant = "primary", testid, children, className }) => (
  <a href={href} data-testid={testid} className={cn(BTN, VARIANTS[variant], className)}>
    <span>{children}</span>
    <Arrow />
  </a>
);

export const TextLink = ({ to, state, testid, children, className }) => (
  <Link to={to} state={state} data-testid={testid}
    className={cn("group inline-flex items-center gap-1.5 text-sm font-medium text-o-accent underline-offset-4 hover:underline", className)}>
    {children}
    <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
  </Link>
);

export const Section = ({ id, theme, className, testid, children }) => (
  <section id={id} data-testid={testid} className={cn(theme, "relative bg-o-bg text-o-text", className)}>
    {children}
  </section>
);

export const SectionTitle = ({ children, className }) => (
  <h2 className={cn("font-display text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl", className)}>
    {children}
  </h2>
);

export const StatusMarker = ({ testid }) => (
  <div data-testid={testid} title="Developed architecture / functioning reference implementation / proposed commercial offering"
    className="inline-flex flex-wrap items-center gap-2 border border-dashed border-o-strong px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-o-dim">
    <span aria-hidden className="h-1.5 w-1.5 rounded-full border border-o-dim" />
    Status: to be confirmed
    <span className="hidden opacity-70 xl:inline">· architecture / reference implementation / proposed offering</span>
  </div>
);

export const Wordmark = ({ testid }) => (
  <Link to="/" data-testid={testid} aria-label="Ordo Intelligence home"
    className="inline-flex items-baseline gap-2 font-display text-[15px] font-extrabold uppercase tracking-[0.24em] text-o-text">
    Ordo<span className="font-medium text-o-dim">Intelligence</span>
  </Link>
);
